import { Button } from '@shared/components/ui/button';
import { Label } from '@shared/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@shared/components/ui/shadcn/dialog';
import { ScrollArea } from '@shared/components/ui/shadcn/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/shadcn/table';
import { cn } from '@shared/utils/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { AccountingEntrySource } from '../../lib/accountingEntryDisplay';
import { parseAccountingEntryObject } from '../../lib/accountingEntryDisplay';

const AMOUNT_KEY = /amount|debit|credit|balance|total|importe|saldo|precio/i;

const formatAmount = (value: number) => {
  try {
    return new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
  } catch {
    return String(value);
  }
};

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  v != null && typeof v === 'object' && !Array.isArray(v);

const humanizeKeyFallback = (key: string) =>
  key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^\w/, (c) => c.toUpperCase());

const pickScalarString = (obj: Record<string, unknown>, keys: string[]): string => {
  for (const k of keys) {
    const v = obj[k];
    if (v == null) continue;
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
    if (typeof v === 'boolean') return v ? '1' : '';
  }
  return '';
};

const formatLedgerDate = (value: unknown): string => {
  if (value == null) return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (Number.isFinite(n)) {
    const ms = n < 1e11 ? n * 1000 : n;
    const d = new Date(ms);
    if (!Number.isNaN(d.getTime())) {
      return new Intl.DateTimeFormat(undefined, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(d);
    }
  }
  if (typeof value === 'string' && value.trim()) return value.trim();
  return '—';
};

const formatDebitCreditCell = (v: unknown): string => {
  if (v == null || v === '') return '';
  const n = typeof v === 'number' ? v : Number(v);
  if (Number.isFinite(n)) {
    if (n === 0) return '';
    return formatAmount(n);
  }
  return '';
};

interface LedgerLineRow {
  cuenta: string;
  descripcion: string;
  debe: string;
  haber: string;
  tags: string;
}

const normalizeLedgerLine = (line: Record<string, unknown>): LedgerLineRow => {
  const singleCuenta = pickScalarString(line, ['cuenta', 'accountLabel', 'account_label']);
  const code = pickScalarString(line, ['code', 'accountCode', 'account_id', 'accountId', 'accountID']);
  const name = pickScalarString(line, ['accountName', 'account_name', 'name', 'account', 'title']);
  let cuenta = '—';
  if (singleCuenta) cuenta = singleCuenta;
  else if (code || name) cuenta = [code, name].filter(Boolean).join(', ') || '—';

  const descripcion =
    pickScalarString(line, ['description', 'descripcion', 'concept', 'concepto', 'detail', 'memo']) || '—';

  const debitRaw = line.debit ?? line.debe ?? line.Debe;
  const creditRaw = line.credit ?? line.haber ?? line.Haber;
  const debe = formatDebitCreditCell(debitRaw);
  const haber = formatDebitCreditCell(creditRaw);

  let tags = '—';
  const tagVal = line.tags ?? line.tag;
  if (Array.isArray(tagVal)) {
    const joined = tagVal.map(String).filter(Boolean).join(', ');
    tags = joined || '—';
  } else if (typeof tagVal === 'string' && tagVal.trim()) {
    tags = tagVal.trim();
  }

  return { cuenta, descripcion, debe, haber, tags };
};

const ReadOnlyField = ({ label, value, className }: { label: string; value: string; className?: string }) => (
  <div className={cn('space-y-1.5', className)}>
    <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
    <div
      className={cn(
        'flex min-h-10 w-full items-center rounded-md border border-input bg-muted/20 px-3 py-2 text-sm text-foreground',
        'transition-colors',
      )}
    >
      <span className="truncate" title={value && value !== '—' ? value : undefined}>
        {value || '—'}
      </span>
    </div>
  </div>
);

interface AccountingEntryFormattedModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: AccountingEntrySource | null;
}

export const AccountingEntryFormattedModal = ({ open, onOpenChange, source }: AccountingEntryFormattedModalProps) => {
  const { t } = useTranslation('accountingSyncModule');

  const parsed = useMemo(() => (source ? parseAccountingEntryObject(source) : null), [source]);

  const { fechaLabel, ledgerLines, metaRows } = useMemo(() => {
    if (!parsed) {
      return {
        fechaLabel: '—',
        ledgerLines: [] as LedgerLineRow[],
        metaRows: [] as Array<{ key: string; label: string; value: string }>,
      };
    }

    const fecha = formatLedgerDate(parsed.date);

    const rawLines = parsed.lines;
    const lineArr = Array.isArray(rawLines) ? rawLines : [];
    const ledgerLinesInner: LedgerLineRow[] = lineArr.map((line) =>
      isPlainObject(line) ? normalizeLedgerLine(line) : normalizeLedgerLine({}),
    );

    const skip = new Set([
      'lines',
      'date',
      'type',
      'entryType',
      'entry_type',
      'tipo',
      'kind',
      'entryKind',
      'notes',
      'notas',
      'memo',
      'comment',
      'comments',
    ]);
    const rows: Array<{ key: string; label: string; value: string }> = [];
    for (const [key, val] of Object.entries(parsed)) {
      if (skip.has(key)) continue;
      if (val === undefined) continue;
      const label = t(`entry_field.${key}`, { defaultValue: humanizeKeyFallback(key) });
      let display: string;
      if (key.endsWith('_at')) {
        display = formatLedgerDate(val);
      } else if (typeof val === 'number' && AMOUNT_KEY.test(key)) {
        display = formatAmount(val);
      } else if (typeof val === 'boolean') {
        display = val ? t('entry_modal.yes') : t('entry_modal.no');
      } else if (typeof val === 'object' && val !== null) {
        display = JSON.stringify(val);
      } else {
        display = val === null || val === '' ? '—' : String(val);
      }
      rows.push({ key, label, value: display });
    }

    return {
      fechaLabel: fecha,
      ledgerLines: ledgerLinesInner,
      metaRows: rows,
    };
  }, [parsed, t]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'max-w-4xl w-[calc(100vw-1.5rem)] max-h-[92vh] flex flex-col gap-0 p-0 overflow-hidden',
          'bg-card border-card-border text-foreground shadow-lg',
        )}
      >
        <DialogHeader className="px-6 pt-5 pb-4 shrink-0 border-b border-card-border space-y-1">
          <DialogTitle className="text-left text-xl font-semibold tracking-tight pr-10">
            {t('entry_modal.title')}
          </DialogTitle>
          <p className="text-xs text-muted-foreground font-normal text-left">{t('entry_modal.readonly_hint')}</p>
        </DialogHeader>

        <ScrollArea className="flex-1 min-h-0 max-h-[min(62vh,calc(92vh-10rem))]">
          <div className="px-6 py-5 space-y-6">
            {parsed && (
              <>
                <div className="max-w-xs">
                  <ReadOnlyField label={t('entry_modal.date_label')} value={fechaLabel} />
                </div>

                <div className="rounded-lg border border-card-border bg-card-bg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-card-border hover:bg-transparent bg-muted/50">
                        <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                          {t('entry_modal.col_account')}
                        </TableHead>
                        <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                          {t('entry_modal.col_description')}
                        </TableHead>
                        <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wide text-right w-[100px]">
                          {t('entry_modal.col_debit')}
                        </TableHead>
                        <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wide text-right w-[100px]">
                          {t('entry_modal.col_credit')}
                        </TableHead>
                        <TableHead className="text-xs font-semibold text-muted-foreground uppercase tracking-wide min-w-[80px]">
                          {t('entry_modal.col_tags')}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {ledgerLines.length === 0 ? (
                        <TableRow className="border-card-border">
                          <TableCell colSpan={5} className="text-sm text-muted-foreground text-center py-8">
                            {t('entry_modal.empty_structured')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        ledgerLines.map((row, idx) => (
                          <TableRow key={idx} className="border-card-border bg-card-bg/30">
                            <TableCell className="text-sm align-top max-w-[220px] text-foreground">
                              {row.cuenta}
                            </TableCell>
                            <TableCell className="text-sm align-top text-foreground">{row.descripcion}</TableCell>
                            <TableCell className="text-sm align-top text-right tabular-nums text-foreground">
                              {row.debe || ''}
                            </TableCell>
                            <TableCell className="text-sm align-top text-right tabular-nums text-foreground">
                              {row.haber || ''}
                            </TableCell>
                            <TableCell className="text-xs align-top text-muted-foreground">{row.tags}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {metaRows.length > 0 && (
                  <details className="rounded-lg border border-dashed border-card-border bg-muted/10 px-4 py-3 group">
                    <summary className="text-sm font-medium text-muted-foreground cursor-pointer list-none flex items-center gap-2">
                      <span className="transition-transform group-open:rotate-90 inline-block text-xs">▸</span>
                      {t('entry_modal.extra_fields')}
                    </summary>
                    <dl className="mt-3 space-y-2 border-t border-card-border pt-3">
                      {metaRows.map((r) => (
                        <div key={r.key} className="grid grid-cols-1 sm:grid-cols-3 gap-1 text-sm">
                          <dt className="text-muted-foreground sm:col-span-1">{r.label}</dt>
                          <dd className="text-foreground sm:col-span-2 break-words">{r.value}</dd>
                        </div>
                      ))}
                    </dl>
                  </details>
                )}
              </>
            )}

            {!parsed && source && <p className="text-sm text-muted-foreground">{t('entry_modal.parse_hint')}</p>}
          </div>
        </ScrollArea>

        <div className="shrink-0 border-t border-card-border bg-card-bg/80 px-6 py-4 flex justify-end">
          <Button type="button" variant="default" size="sm" onClick={() => onOpenChange(false)}>
            {t('entry_modal.close')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
